import { useState } from 'react';
import type { AuditLegalHoldItem, AuditLegalHoldStatusFilter } from '../../api/adminOps';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../../components/ui';
import { useAuditLegalHolds } from '../../hooks/useAdminOps';
import {
  LEGAL_HOLD_FILTERS,
  LegalHoldStatusFilter,
  ReferenceLabel,
  heldPartitions,
  legalHoldStatusChip,
  partitionsSummary,
} from '../auditRetention.logic';
import {
  DetailField,
  DetailSection,
  FilterChip,
  LoadError,
  absolute,
  relative,
} from '../websiteLeads/parts';
import { HoldActions, PlaceHoldModal } from './LegalHoldForms';
import { PartitionList, monoValue } from './parts';

function HoldDetail({ hold }: { hold: AuditLegalHoldItem }) {
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusChip {...legalHoldStatusChip(hold.status)} />
      </div>
      <DetailSection title="Hold">
        <DetailField label={ReferenceLabel.CASE} value={monoValue(hold.case_id)} />
        <DetailField label={ReferenceLabel.APPROVAL} value={monoValue(hold.approval_ref)} />
        <DetailField label="Placed" value={absolute(hold.placed_at)} />
        <DetailField label="Released" value={absolute(hold.released_at)} />
        <DetailField
          label="Release approval reference"
          value={monoValue(hold.release_approval_ref)}
        />
        <DetailField label="Hold id" value={monoValue(hold.hold_id)} />
      </DetailSection>
      <DetailSection title="Partitions held">
        <PartitionList partitions={heldPartitions(hold)} />
      </DetailSection>
      <DetailSection title="Extensions">
        {hold.extensions.length === 0 ? (
          <p className="text-sm text-gray-text">This hold has not been extended.</p>
        ) : (
          <ol className="space-y-3">
            {hold.extensions.map(extension => (
              <li
                key={`${extension.approval_ref}-${extension.extended_at}`}
                className="rounded-lg border border-gray-border p-3 space-y-2"
              >
                <div className="flex justify-between gap-3 text-sm">
                  <span className="font-mono text-white break-all">{extension.approval_ref}</span>
                  <span className="text-gray-text whitespace-nowrap">
                    {absolute(extension.extended_at)}
                  </span>
                </div>
                <PartitionList partitions={extension.added_partitions} />
              </li>
            ))}
          </ol>
        )}
      </DetailSection>
    </>
  );
}

export default function LegalHoldsTab({ canWrite }: { canWrite: boolean }) {
  const [status, setStatus] = useState<AuditLegalHoldStatusFilter>(LegalHoldStatusFilter.ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);
  const queue = useAuditLegalHolds({ status });
  const selected = queue.rows.find(row => row.hold_id === selectedId) ?? null;
  const closeDrawer = () => setSelectedId(null);

  const columns: QueueColumn<AuditLegalHoldItem>[] = [
    {
      key: 'placed',
      header: 'Placed',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">{relative(row.placed_at)}</span>
      ),
    },
    {
      key: 'case',
      header: 'Case',
      render: row => <span className="font-mono text-xs break-all">{row.case_id}</span>,
    },
    {
      key: 'partitions',
      header: 'Partitions',
      render: row => (
        <span className="font-mono text-xs">{partitionsSummary(heldPartitions(row))}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => <StatusChip {...legalHoldStatusChip(row.status)} />,
    },
    {
      key: 'extensions',
      header: 'Extensions',
      render: row => <span className="tabular-nums">{row.extensions.length}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Hold status">
          {LEGAL_HOLD_FILTERS.map(filter => (
            <FilterChip
              key={filter.value}
              active={status === filter.value}
              label={filter.label}
              onClick={() => {
                setStatus(filter.value);
                setSelectedId(null);
              }}
            />
          ))}
        </div>
        {canWrite && (
          <button className="btn-primary ml-auto" onClick={() => setPlacing(true)}>
            Place legal hold
          </button>
        )}
      </div>

      {queue.isError ? (
        <LoadError what="legal holds" error={queue.error} retry={() => queue.refetch()} />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.hold_id}
          isLoading={queue.isPending}
          onRowClick={row => setSelectedId(row.hold_id)}
          rowActionLabel={row => `Open hold ${row.case_id}`}
          emptyTitle="No legal holds"
          emptyHint={
            status === LegalHoldStatusFilter.ALL
              ? 'No audit partition has been placed under a legal hold.'
              : `No ${status} legal holds.`
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {placing && <PlaceHoldModal onClose={() => setPlacing(false)} />}

      <DetailDrawer
        open={selected !== null}
        title={selected ? `Legal hold ${selected.case_id}` : 'Legal hold'}
        onClose={closeDrawer}
        footer={
          selected && (
            <HoldActions
              key={selected.hold_id}
              hold={selected}
              canWrite={canWrite}
              onDone={closeDrawer}
            />
          )
        }
      >
        {selected && <HoldDetail hold={selected} />}
      </DetailDrawer>
    </div>
  );
}
